import React, { CSSProperties, FC, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { formatMinutes, getDelayInMinutes, isDelayed } from '../shared/procedureUtils';

const Wrapper = styled.div<any>`
  font-size: 0.75em;
  display: flex;
  justify-content: space-between;
  background-color: ${props => (props.hasError ? 'rgba(255, 137, 0, 0.25)' : 'rgba(255, 255, 255, 0.1)')};
  padding: 0.375em;
  margin-top: 1px;

  > div {
    display: flex;
    align-items: center;
  }
`;

export const Delay = styled.div<any>`
  font-weight: 600;
  color: ${props => props.theme.backgroundColor.string()};
  background-color: ${props => props.theme.warningColor.string()};
  border-radius: 0.125em;
  padding: 0.15em 0.25em 0.125em;
  margin-right: 0.5em;
  ${props =>
    props.subtle &&
    css`
      opacity: ${props => (props.theme.backgroundColor.string() === 'rgb(255, 255, 255)' ? '1' : '.6')};
      background-color: transparent;
      color: ${props => (props.theme.backgroundColor.string() === 'rgb(255, 255, 255)' ? '#00A7F7' : '#abe3ff')};
      font-weight: 500;
      padding-left: 0;
      padding-right: 0;
    `}
`;

const ReadyTag = styled.div<any>`
  font-weight: 600;
  color: #002707;
  background-color: ${props => props.theme.successColor.string()};
  border-radius: 0.125em;
  padding: 0.15em 0.25em 0.125em;
  margin-left: 0.5em;
`;
const ReadyForAnesthesiaTag = styled.div<any>`
  font-weight: 600;
  color: white;
  background-color: purple;
  border-radius: 0.125em;
  padding: 0.15em 0.25em 0.125em;
  margin-left: 0.5em;
`;
const SpinalTimeoutTag = styled.div<any>`
  font-weight: 600;
  color: white;
  background-color: #000000;
  border-radius: 0.125em;
  padding: 0.15em 0.25em 0.125em;
  margin-left: 0.5em;
`;

const AltReadyForSurgeonTag = styled.div<any>`
  font-weight: 600;
  color: black;
  background-color: #00a7f7;
  border-radius: 0.125em;
  padding: 0.15em 0.25em 0.125em;
  margin-left: 0.5em;
`;

export const HelpTag = styled.div<any>`
  font-weight: 600;
  color: white;
  background-color: red;
  border-radius: 0.125em;
  padding: 0.25em 0.5em 0.25em;
  margin-right: 0.75em;
  font-size: 1.1em;
  ${props =>
    props.subtle &&
    css`
      opacity: 0.6;
      background-color: transparent;
      color: #abe3ff;
      font-weight: 500;
      padding-left: 0;
      padding-right: 0;
      font-size: 1em;
    `}
`;

export const FlipRoomTag = styled.div<any>`
  font-weight: 600;
  color: white;
  background-color: #6c9911;
  border-radius: 0.125em;
  padding: 0.25em 0.5em 0.25em;
  margin-right: 0.75em;
  font-size: 1.1em;
  ${props =>
    props.subtle &&
    css`
      opacity: 0.6;
      background-color: transparent;
      color: #abe3ff;
      font-weight: 500;
      padding-left: 0;
      padding-right: 0;
      font-size: 1em;
    `}
`;

const MedsDeliveredTag = styled.div<any>`
  font-weight: 600;
  color: white;
  background-color: #17a2b8;
  border-radius: 0.125em;
  padding: 0.15em 0.25em 0.125em;
  margin-left: 0.5em;
`;

const ExternalPharmacyTag = styled.div<any>`
  font-weight: 600;
  color: white;
  background-color: #ff9800;
  border-radius: 0.125em;
  padding: 0.15em 0.25em 0.125em;
  margin-left: 0.5em;
`;

const Appendix: FC<{
  isDPU?: boolean;
  isDimmed?: boolean;
  isDelayed?: boolean;
  isWaiting?: boolean;
  late?: number | null;
  delay?: number;
  readyForOr?: boolean;
  readyForSurgeon?: boolean;
  readyForSurgery?: boolean;
  isHoldProcedure?: boolean;
  isBlockNerve?: boolean;
  style?: CSSProperties;
  isHelp?: boolean;
  isFlipRoom?: boolean;
  messagingView?: boolean;
  readyForAnesthesia?: boolean;
  isSpinalTimeout?: boolean;
  scheduleProcedure?: any;
  isMiddleEast?: boolean;
  hospitalId?: string;
  medsDeliveryStatus?: string | null;
}> = ({
  isDPU,
  isDimmed,
  isDelayed,
  isWaiting,
  late,
  delay,
  readyForOr,
  readyForSurgeon,
  readyForSurgery,
  isHoldProcedure,
  isBlockNerve,
  style,
  isHelp,
  isFlipRoom,
  messagingView,
  readyForAnesthesia,
  isSpinalTimeout,
  scheduleProcedure,
  isMiddleEast: isMiddleEastProp,
  hospitalId,
  medsDeliveryStatus,
}) => {
  const isAltReadyForSurgeon =
    (window.location.hostname === 'view5.ospitek.com' && (Number(hospitalId) === 1 || Number(hospitalId) === 2)) ||
    (window.location.hostname === 'testing.ospitek.com' && Number(hospitalId) === 152);

  const visible = [
    readyForOr && !isDPU,
    readyForSurgeon && !isDPU,
    readyForSurgery,
    isDelayed,
    isHoldProcedure,
    isBlockNerve,
    isHelp,
    isFlipRoom,
    readyForAnesthesia,
    isSpinalTimeout,
    (late || 0) > 0,
    !!medsDeliveryStatus,
  ].includes(true);

  return visible && !messagingView ? (
    <Wrapper style={style}>
      <div>
        {isHelp && <HelpTag>HELP</HelpTag>}
        {isFlipRoom && <FlipRoomTag>Flip Room</FlipRoomTag>}
        <DelaySlot scheduleProcedure={scheduleProcedure} isMiddleEast={isMiddleEastProp} isDimmed={isDimmed} />
        {isDimmed && <Delay subtle>•</Delay>}
        {late && <Delay subtle={!isWaiting || isDimmed}>OR IN late {formatMinutes(late)}</Delay>}
      </div>
      {!isDimmed && (
        <div>
          {readyForAnesthesia && !isBlockNerve && <ReadyForAnesthesiaTag>Ready For Anes</ReadyForAnesthesiaTag>}
          {isSpinalTimeout && <SpinalTimeoutTag>Spinal Timeout</SpinalTimeoutTag>}
          {readyForOr && !isDPU && <ReadyTag>Ready For OR</ReadyTag>}
          {readyForSurgeon &&
            !readyForOr &&
            !isDPU &&
            (isAltReadyForSurgeon ? (
              <AltReadyForSurgeonTag>Ready For Surgeon</AltReadyForSurgeonTag>
            ) : (
              <ReadyTag>Ready For Surgeon</ReadyTag>
            ))}
          {!isDimmed && <div>{readyForSurgery && <ReadyTag>Ready For Surgery</ReadyTag>}</div>}
          {isHoldProcedure && <Delay>On Hold</Delay>}
          {isBlockNerve && <Delay>Block Nerve</Delay>}
          {medsDeliveryStatus === 'Delivered' && <MedsDeliveredTag>Meds Delivered</MedsDeliveredTag>}
          {medsDeliveryStatus === 'ExternalPharmacy' && <ExternalPharmacyTag>External Pharmacy</ExternalPharmacyTag>}
        </div>
      )}
    </Wrapper>
  ) : null;
};

export default Appendix;

const DelaySlot = ({ scheduleProcedure, isMiddleEast, isDimmed }) => {
  const initialState = [isDelayed(scheduleProcedure, isMiddleEast), getDelayInMinutes(scheduleProcedure, new Date())];
  const currentState = useRef(initialState);
  const [[delayed, delay], setState] = useState(initialState);

  useEffect(() => {
    const interval = setInterval(() => {
      const delayed = isDelayed(scheduleProcedure, isMiddleEast);
      const delay = getDelayInMinutes(scheduleProcedure, new Date());

      const prevDelayed = currentState.current[0];
      const prevDelay = currentState.current[1];

      currentState.current = [delayed, delay];

      if (prevDelayed !== delayed || prevDelay !== delay) {
        setState([delayed, delay]);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [scheduleProcedure, isMiddleEast]);

  return delayed ? <Delay subtle={isDimmed}>OR OUT late {formatMinutes(delay)}</Delay> : null;
};
